import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import investorsService, {
  Investor,
  InvestorInput,
  InvestorOpportunityStage,
  InvestorType,
  ListInvestorsParams
} from '../../../services/investorCrm/investorsService';
import { useInvestorCrmOrgStore } from '../../../store/investorCrmOrg.store';
import InvestorDrawer from '../../../components/investor-crm/InvestorDrawer';

type ViewMode = 'table' | 'kanban' | 'gallery';

const STAGE_OPTIONS: Array<{ value: InvestorOpportunityStage; label: string }> = [
  { value: 'prospecting', label: 'Prospecting' },
  { value: 'warm_intro', label: 'Warm intro' },
  { value: 'first_meeting', label: 'First meeting' },
  { value: 'diligence', label: 'Due diligence' },
  { value: 'term_sheet', label: 'Term sheet' },
  { value: 'committed', label: 'Committed' },
  { value: 'closed_won', label: 'Closed won' },
  { value: 'closed_lost', label: 'Closed lost' }
];

const INVESTOR_TYPE_OPTIONS: Array<{ value: InvestorType; label: string }> = [
  { value: 'individual', label: 'Individual' },
  { value: 'angel_group', label: 'Angel group' },
  { value: 'venture_capital', label: 'Venture capital' },
  { value: 'private_equity', label: 'Private equity' },
  { value: 'family_office', label: 'Family office' },
  { value: 'corporate_venture', label: 'Corporate venture' },
  { value: 'accelerator_incubator', label: 'Accelerator / incubator' },
  { value: 'sovereign_wealth', label: 'Sovereign wealth' },
  { value: 'other', label: 'Other' }
];

const InvestorsTab: React.FC = () => {
  const { activeOrgId } = useInvestorCrmOrgStore();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [items, setItems] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<InvestorOpportunityStage | ''>('');
  const [investorTypeFilter, setInvestorTypeFilter] = useState<InvestorType | ''>('');
  
  // Simplified form state - only essentials
  const [companyName, setCompanyName] = useState('');
  const [opportunityStage, setOpportunityStage] = useState<InvestorOpportunityStage>('prospecting');
  const [contactName, setContactName] = useState('');
  const quickAddRef = useRef<HTMLInputElement | null>(null);
  
  const [drawerInvestorId, setDrawerInvestorId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!activeOrgId) return;

    setLoading(true);
    try {
      const params: ListInvestorsParams = {
        organization_id: activeOrgId,
        limit: 100,
        offset: 0,
        search: search.trim() || undefined,
        opportunity_stage: stageFilter || undefined,
        investor_type: investorTypeFilter || undefined
      };

      const { data } = await investorsService.list(params);
      setItems(data || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load investors');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, search, stageFilter, investorTypeFilter]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const createInvestor = useCallback(async () => {
    if (!activeOrgId) {
      toast.error('Please select an organization first');
      return;
    }

    if (!companyName.trim()) {
      toast.error('Company name is required');
      return;
    }

    setLoading(true);
    try {
      const payload: InvestorInput = {
        organization_id: activeOrgId,
        company_name: companyName.trim(),
        contact_name: contactName.trim() || undefined,
        opportunity_stage: opportunityStage,
        investor_type: 'individual'
      };

      await investorsService.create(payload);
      setCompanyName('');
      setContactName('');
      setOpportunityStage('prospecting');
      toast.success('Investor added');
      await fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create investor');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, companyName, contactName, opportunityStage, fetchData]);

  const groupedByStage = useMemo(() => {
    const grouped: Record<string, Investor[]> = {};
    STAGE_OPTIONS.forEach((stageOpt) => {
      const stage = stageOpt.value;
      grouped[stage] = items.filter(item => item.opportunity_stage === stage);
    });
    return grouped;
  }, [items]);

  return (
    <div className="space-y-6">
      {/* Filters & View Switcher */}
      <div className="flex items-center justify-between gap-4">
        <input
          type="text"
          className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          placeholder="Search investors..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as InvestorOpportunityStage | '')}
        >
          <option value="">All Stages</option>
          {STAGE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
          value={investorTypeFilter}
          onChange={(e) => setInvestorTypeFilter(e.target.value as InvestorType | '')}
        >
          <option value="">All Types</option>
          {INVESTOR_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        
        {/* View Mode Switcher */}
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-1">
          <button
            onClick={() => setViewMode('table')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'table' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Table view"
          >
            📋
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'kanban' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Kanban view"
          >
            📊
          </button>
          <button
            onClick={() => setViewMode('gallery')}
            className={`rounded px-3 py-1.5 text-sm font-medium transition ${
              viewMode === 'gallery' ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-100'
            }`}
            title="Gallery view"
          >
            🎴
          </button>
        </div>
      </div>

      {/* Compact Quick Add Form */}
      <div className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
        <h3 className="mb-4 text-sm font-semibold text-gray-700">➕ Quick Add Investor</h3>
        <div className="grid gap-3 md:grid-cols-4">
          <input
            ref={quickAddRef}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Company name *"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <select
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            value={opportunityStage}
            onChange={(e) => setOpportunityStage(e.target.value as InvestorOpportunityStage)}
          >
            {STAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="Contact name (optional)"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
          />
          <button
            onClick={() => void createInvestor()}
            disabled={loading}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {/* Views */}
      {viewMode === 'table' && (
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">
              {search || stageFilter ? 'No investors match your filters.' : 'No investors yet. Add one above!'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Company</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Contact</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Stage</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Check Size</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((investor) => (
                    <tr key={investor.id} className="transition hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-primary">{investor.company_name}</div>
                        {investor.fund_focus && (
                          <div className="text-xs text-gray-500">{investor.fund_focus}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{investor.contact_name || '—'}</div>
                        {investor.contact_email && (
                          <div className="text-xs text-gray-500">{investor.contact_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                          {INVESTOR_TYPE_OPTIONS.find(t => t.value === investor.investor_type)?.label || 'Individual'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {STAGE_OPTIONS.find(s => s.value === investor.opportunity_stage)?.label || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {investor.check_size_min != null ? (
                          `£${Intl.NumberFormat().format(investor.check_size_min)}${investor.check_size_max != null ? `-£${Intl.NumberFormat().format(investor.check_size_max)}` : '+'}`
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => {
                            setDrawerInvestorId(investor.id);
                            setIsDrawerOpen(true);
                          }}
                          className="rounded-lg border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewMode === 'kanban' && (
        <div className="grid gap-4 md:grid-cols-4">
          {STAGE_OPTIONS.slice(0, 4).map((stageOpt) => (
            <div key={stageOpt.value} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <h4 className="mb-3 text-sm font-semibold text-gray-700">{stageOpt.label}</h4>
              <div className="space-y-2">
                {groupedByStage[stageOpt.value]?.map((investor) => (
                  <div
                    key={investor.id}
                    className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-3 transition hover:shadow-md"
                    onClick={() => {
                      setDrawerInvestorId(investor.id);
                      setIsDrawerOpen(true);
                    }}
                  >
                    <div className="font-medium text-gray-900">{investor.company_name}</div>
                    {investor.contact_name && <div className="text-xs text-gray-500">{investor.contact_name}</div>}
                  </div>
                ))}
                {groupedByStage[stageOpt.value]?.length === 0 && (
                  <div className="py-4 text-center text-xs text-gray-400">No investors</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewMode === 'gallery' && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {items.map((investor) => (
            <div
              key={investor.id}
              className="cursor-pointer rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-md"
              onClick={() => {
                setDrawerInvestorId(investor.id);
                setIsDrawerOpen(true);
              }}
            >
              <div className="mb-2 text-2xl">💼</div>
              <h4 className="mb-1 font-semibold text-gray-900">{investor.company_name}</h4>
              <p className="mb-2 text-xs text-gray-500">{investor.fund_focus || INVESTOR_TYPE_OPTIONS.find(t => t.value === investor.investor_type)?.label}</p>
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                {STAGE_OPTIONS.find(s => s.value === investor.opportunity_stage)?.label || 'Prospecting'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Investor Details Drawer */}
      <InvestorDrawer
        organizationId={activeOrgId ?? null}
        investorId={drawerInvestorId}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onUpdated={() => void fetchData()}
      />
    </div>
  );
};

export default InvestorsTab;
